import { useEffect, useCallback, useRef } from 'react';

const KONAMI_CODE = [
  'ArrowUp',
  'ArrowUp',
  'ArrowDown',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'ArrowLeft',
  'ArrowRight',
  'KeyB',
  'KeyA',
];

interface UseKonamiCodeOptions {
  onActivate: () => void;
  enabled?: boolean;
  timeout?: number;
}

/**
 * Hook to detect Konami code input sequence
 * ↑ ↑ ↓ ↓ ← → ← → B A
 */
export function useKonamiCode({
  onActivate,
  enabled = true,
  timeout = 2000,
}: UseKonamiCodeOptions) {
  const inputSequence = useRef<string[]>([]);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  const resetSequence = useCallback(() => {
    inputSequence.current = [];
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);
  
  useEffect(() => {
    if (!enabled) {
      resetSequence();
      return;
    }
    
    const handleKeyDown = (event: KeyboardEvent) => {
      // Don't trigger if user is typing in an input
      if (
        event.target instanceof HTMLInputElement ||
        event.target instanceof HTMLTextAreaElement ||
        (event.target as HTMLElement)?.isContentEditable
      ) {
        return;
      }
      
      // Clear timeout and reset if too much time passed
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      
      // Add key to sequence
      inputSequence.current.push(event.code);
      
      // Keep only the last N keys (length of Konami code)
      if (inputSequence.current.length > KONAMI_CODE.length) {
        inputSequence.current = inputSequence.current.slice(-KONAMI_CODE.length);
      }
      
      // Check if sequence matches
      const matches = inputSequence.current.every(
        (key, index) => key === KONAMI_CODE[index]
      );
      
      if (matches && inputSequence.current.length === KONAMI_CODE.length) {
        onActivate();
        resetSequence();
      } else {
        // Set timeout to reset sequence
        timeoutRef.current = setTimeout(resetSequence, timeout);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      resetSequence();
    };
  }, [enabled, onActivate, timeout, resetSequence]);
  
  return { reset: resetSequence };
}
